<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
} from 'chart.js';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import KanbanReportWidget from 'kanban/components/reports/KanbanReportWidget.vue';
import { formatPeriodLabel } from 'kanban/helper/reportFormatters';
import { stepColor, CFD_GROUP_BY } from 'kanban/constants/reports';

const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  groupBy: {
    type: String,
    default: 'day',
  },
  locale: {
    type: String,
    default: 'en',
  },
});

const emit = defineEmits(['retry', 'update:groupBy']);

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler
);

const { t } = useI18n();

const groupByLabel = key =>
  t(`KANBAN.REPORTS.CFD.GROUP_BY.${key.toUpperCase()}`);
const groupByOptions = computed(() =>
  CFD_GROUP_BY.map(value => ({ value, label: groupByLabel(value) }))
);

const series = computed(() => props.data?.series || []);
const dates = computed(() => props.data?.dates || []);

const isEmpty = computed(
  () =>
    !series.value.length ||
    series.value.every(line => line.points.every(point => !point.count))
);

const hexToRgba = (hex, alpha) => {
  const value = hex.replace('#', '');
  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const labelFor = iso => formatPeriodLabel(iso, props.groupBy, props.locale);

const collection = computed(() => ({
  labels: dates.value.map(labelFor),
  datasets: series.value.map((line, index) => {
    const color = stepColor(line.color, index);
    return {
      label: line.name,
      data: line.points.map(point => point.count),
      borderColor: color,
      backgroundColor: hexToRgba(color, 0.45),
      fill: true,
      tension: 0.3,
      pointRadius: 0,
      borderWidth: 1.5,
    };
  }),
}));

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 0 },
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: { display: true, position: 'bottom', labels: { boxWidth: 12 } },
  },
  scales: {
    x: { stacked: true, grid: { display: false } },
    y: { stacked: true, beginAtZero: true, grid: { drawOnChartArea: true } },
  },
};
</script>

<template>
  <KanbanReportWidget
    :title="t('KANBAN.REPORTS.CFD.TITLE')"
    :info-text="t('KANBAN.REPORTS.CFD.INFO')"
    :is-loading="isLoading"
    :error="error"
    :is-empty="isEmpty"
    :empty-text="t('KANBAN.REPORTS.CFD.EMPTY')"
    @retry="emit('retry')"
  >
    <template #actions>
      <SelectMenu
        :model-value="groupBy"
        :options="groupByOptions"
        :label="groupByLabel(groupBy)"
        max-width="max-w-36"
        sub-menu-position="bottom"
        @update:model-value="emit('update:groupBy', $event)"
      />
    </template>
    <div class="h-80">
      <Line :data="collection" :options="chartOptions" />
    </div>
  </KanbanReportWidget>
</template>
